/**
 * <UpcomingSessionsWidget /> — Phase 14 Session 1.
 *
 * Timezone-aware upcoming-sessions display shared by:
 *   - Tutor dashboard ("My Upcoming Sessions")
 *   - Student dashboard ("My Upcoming Classes")
 * Reads `/api/me/{role}/upcoming-sessions` and renders per-viewer local
 * time + a secondary line with the counterparty's local time.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, Clock, Video, RefreshCcw, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const WINDOWS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'upcoming', label: 'Upcoming' },
];

const formatCountdown = (seconds) => {
  if (seconds == null) return '';
  if (seconds < 0) return 'Started';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `Starts in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Starts in ${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `Starts in ${d}d ${h % 24}h`;
};

const UpcomingSessionsWidget = ({ role = 'tutor', title }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState('upcoming');
  const [viewer, setViewer] = useState({ tz: '', confirmed: false });

  const endpoint = role === 'tutor' ? '/api/me/tutor/upcoming-sessions' : '/api/me/student/upcoming-sessions';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(endpoint, { params: { window, limit: 30 } });
      setItems(r.data?.items || []);
      setViewer({ tz: r.data?.viewer_tz, confirmed: r.data?.viewer_tz_confirmed });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load upcoming sessions');
    } finally { setLoading(false); }
  }, [endpoint, window]);

  useEffect(() => { load(); }, [load]);

  const label = title || (role === 'tutor' ? 'My Upcoming Sessions' : 'My Upcoming Classes');

  return (
    <Card data-testid={`upcoming-sessions-${role}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-red-700" /> {label}
          </CardTitle>
          <div className="text-[10px] text-gray-500 mt-1">
            Your timezone: <span className="font-mono">{viewer.tz || 'Australia/Melbourne'}</span>
            {!viewer.confirmed && (
              <span className="ml-1 text-amber-700">· Timezone not confirmed</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWindow(w.key)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                window === w.key ? 'bg-red-700 border-red-700 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`upcoming-${role}-window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
          <button onClick={load} className="ml-1 p-1 rounded hover:bg-gray-100" aria-label="Refresh">
            <RefreshCcw className="h-3.5 w-3.5 text-gray-500" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs italic text-gray-400 py-6 text-center border border-dashed rounded-lg">
            No sessions in this window.
          </div>
        ) : (
          items.map((it) => <SessionRow key={it.session_id} item={it} role={role} />)
        )}
      </CardContent>
    </Card>
  );
};

const SessionRow = ({ item, role }) => {
  const v = item.display?.viewer || {};
  // PRIVACY: intentionally do NOT read display.other. Tutors and students
  // must not see the counterparty's timezone name, city or converted time.
  const joinUrl = role === 'tutor' ? item.tutor_join_url : item.student_join_url;
  const counterName = role === 'tutor' ? item.student_name : item.tutor_name;

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`upcoming-${role}-item-${item.session_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-gray-900">
            {item.course_name || item.module_name || 'Session'}
            {item.module_name && item.course_name ? <span className="text-gray-500 font-normal"> · {item.module_name}</span> : null}
          </div>
          <div className="text-xs text-gray-600 mt-0.5">
            {role === 'tutor' ? 'Student' : 'Tutor'}: <span className="font-medium">{counterName || '—'}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-sm">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            <span className="font-bold text-gray-900">
              {v.day}, {v.date} · {v.hhmm_ampm} – {v.end_hhmm_ampm}
            </span>
            {v.tz_abbrev && <span className="text-[10px] text-gray-500 ml-1">{v.tz_abbrev}</span>}
          </div>
          {item.countdown_seconds != null && (
            <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-red-700">
              {formatCountdown(item.countdown_seconds)}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          <Badge variant="outline" className="text-[10px]">{item.status}</Badge>
          {joinUrl && (
            <Button asChild size="sm" className="bg-red-700 hover:bg-red-800 text-white h-7 text-[11px]"
              data-testid={`upcoming-${role}-join-${item.session_id}`}>
              <a href={joinUrl.startsWith('http') ? joinUrl : joinUrl} target="_blank" rel="noopener noreferrer">
                <Video className="h-3 w-3 mr-1" /> Join
              </a>
            </Button>
          )}
        </div>
      </div>
      {!item.display?.viewer_tz_confirmed && (
        <div className="mt-2 text-[10px] text-amber-700 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Your timezone is not confirmed — please contact support to verify.
        </div>
      )}
    </div>
  );
};

export default UpcomingSessionsWidget;
